import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  Sparkles, Send, Loader2, RefreshCw, Copy, Check, 
  Lightbulb, FileText, Users, PoundSterling, Shield,
  Bot, Trash2, Clock
} from 'lucide-react';
import { toast } from 'sonner';

// Quick prompt suggestions
const QUICK_PROMPTS = [
  { icon: <FileText className="w-4 h-4" />, label: "Draft a policy", prompt: "Help me draft a professional policy for " },
  { icon: <Users className="w-4 h-4" />, label: "Staff management", prompt: "Suggest best practices for managing staff in a healthcare setting regarding " },
  { icon: <PoundSterling className="w-4 h-4" />, label: "Financial advice", prompt: "Provide guidance on healthcare business finances for " },
  { icon: <Shield className="w-4 h-4" />, label: "Compliance check", prompt: "Review compliance requirements for ICRCL regarding " },
  { icon: <Lightbulb className="w-4 h-4" />, label: "Business idea", prompt: "Suggest innovative ideas for growing a children's therapy clinic focusing on " },
];

export default function AIAssistantPage() {
  const { api, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const isSuperAdmin = user?.email?.toLowerCase() === 'admin@icrcl.co.uk';

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load chat history from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('icrcl_ai_chat');
    if (saved) {
      try {
        setMessages(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load chat history');
      }
    }
  }, []);

  // Save chat history to localStorage
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem('icrcl_ai_chat', JSON.stringify(messages.slice(-50))); // Keep last 50 messages
    }
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = {
      role: 'user',
      content: input.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      // Build conversation history for context
      const conversationHistory = messages.slice(-10).map(m => ({
        role: m.role,
        content: m.content
      }));
      conversationHistory.push({ role: 'user', content: userMessage.content });

      const response = await api.post('/ai-assistant/chat', {
        messages: conversationHistory,
        context: "You are an AI assistant for ICRCL Healthcare (Innovative Care and Rehab Centre Limited), a children's therapy clinic in London specializing in neuro-rehabilitation, occupational therapy, physiotherapy, speech therapy, and psychology. Help the Super Admin with business decisions, policy drafting, compliance, staff management, and any other queries. Be professional, helpful, and provide actionable advice."
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('AI chat error:', error);
      toast.error(error.response?.data?.detail || 'Failed to get AI response');
      
      // Add error message
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date().toISOString(),
        isError: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const applyQuickPrompt = (prompt) => {
    setInput(prompt);
    textareaRef.current?.focus();
  };

  const clearHistory = () => {
    if (window.confirm('Clear all chat history?')) {
      setMessages([]);
      localStorage.removeItem('icrcl_ai_chat');
      toast.success('Chat history cleared');
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Shield className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-700">Super Admin Only</h3>
            <p className="text-slate-500 mt-2">AI Assistant is exclusively available for Super Admin.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#1e3a5f]">AI Assistant</h1>
            <p className="text-sm text-slate-500">Your intelligent helper for ICRCL</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Badge variant="outline" className="text-purple-600 border-purple-300">
            <Bot className="w-3 h-3 mr-1" />
            Powered by AI
          </Badge>
          {messages.length > 0 && (
            <Button variant="ghost" size="sm" onClick={clearHistory}>
              <Trash2 className="w-4 h-4 mr-1" />
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Quick Prompts */}
      {messages.length === 0 && (
        <Card className="mb-4 border-purple-100 bg-purple-50/50">
          <CardContent className="py-4">
            <p className="text-sm font-medium text-purple-800 mb-3">Quick prompts to get started:</p>
            <div className="flex flex-wrap gap-2">
              {QUICK_PROMPTS.map((qp, idx) => (
                <Button
                  key={idx}
                  variant="outline"
                  size="sm"
                  className="border-purple-200 hover:bg-purple-100 text-purple-700"
                  onClick={() => applyQuickPrompt(qp.prompt)}
                >
                  {qp.icon}
                  <span className="ml-1">{qp.label}</span>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Chat Messages */}
      <Card className="flex-1 overflow-hidden flex flex-col">
        <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-slate-400">
              <Sparkles className="w-16 h-16 mb-4 text-purple-300" />
              <p className="text-lg font-medium">How can I help you today?</p>
              <p className="text-sm">Ask me anything about ICRCL operations, policies, or business advice.</p>
            </div>
          ) : (
            messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    msg.role === 'user'
                      ? 'bg-[#1e3a5f] text-white rounded-br-md'
                      : msg.isError
                      ? 'bg-red-50 text-red-700 border border-red-200 rounded-bl-md'
                      : 'bg-slate-100 text-slate-800 rounded-bl-md'
                  }`}
                >
                  {msg.role === 'assistant' && !msg.isError && (
                    <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-200">
                      <Sparkles className="w-4 h-4 text-purple-500" />
                      <span className="text-xs font-medium text-purple-600">AI Assistant</span>
                    </div>
                  )}
                  <div className="whitespace-pre-wrap text-sm">{msg.content}</div>
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-opacity-20 border-current">
                    <span className="text-xs opacity-60">
                      <Clock className="w-3 h-3 inline mr-1" />
                      {new Date(msg.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {msg.role === 'assistant' && !msg.isError && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 hover:bg-slate-200"
                        onClick={() => copyToClipboard(msg.content, idx)}
                      >
                        {copiedIndex === idx ? (
                          <Check className="w-3 h-3 text-green-600" />
                        ) : (
                          <Copy className="w-3 h-3" />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
          
          {loading && (
            <div className="flex justify-start">
              <div className="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-purple-500" />
                  <span className="text-sm text-slate-500">Thinking...</span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </CardContent>

        {/* Input Area */}
        <div className="p-4 border-t bg-white">
          <div className="flex gap-2">
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask me anything... (Press Enter to send, Shift+Enter for new line)"
              className="min-h-[60px] max-h-[150px] resize-none"
              disabled={loading}
            />
            <Button
              onClick={sendMessage}
              disabled={!input.trim() || loading}
              className="h-auto bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </div>
          <p className="text-xs text-slate-400 mt-2 text-center">
            AI responses are generated and may not always be accurate. Always verify important information.
          </p>
        </div>
      </Card>
    </div>
  );
}
